import { Theme, makeStyles, createStyles, IconButton } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import { FileTypeIcon } from '../../components/icons/FileTypeIcon';
import { RvLabel } from '../../components/Label/Label';
import { MatterTerminology } from '../../MatterTerminology';
import { FormattedDateTime, DateTimeDisplayStyle } from '../../components/DataDisplay/FormattedDateTime';
import DialogBox from '../../Dashboard/Component/DialogBox';
import { MatterDocument } from './hooks/useMatterDocumentQuery';
import { useMatterDocumentUrlQuery } from './hooks/useMatterDocumentUrlQuery';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        card: {
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.75),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(1.5, 2),
        },
        // No coloured circle backdrop here (unlike other icon chips in this app) - FileTypeIcon
        // is a self-coloured badge (a page + coloured label band), so a coloured circle behind
        // it would just double up on colour instead of adding contrast.
        iconChip: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            cursor: 'pointer',
            '& svg': {
                fontSize: 36,
            },
        },
        details: {
            flex: 1,
            minWidth: 0,
        },
        title: {
            cursor: 'pointer',
            display: 'block',
            fontSize: 13.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.35,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        meta: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            marginTop: 2,
            fontSize: 11.5,
            color: theme.palette.text.secondary,
        },
        // FormattedDateTime's own root element is a <div> (block), so nesting it straight
        // after "Uploaded on" pushes the date onto its own line - flattening it back to inline
        // keeps the label and value on one line (same fix as TimelineInfoCard.tsx's own
        // metaLabel class).
        metaLabel: {
            display: 'inline-flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: '0.25em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '& > div': {
                display: 'inline',
            },
        },
        addedByChip: {
            display: 'inline-flex',
            alignItems: 'center',
            flexShrink: 0,
            padding: '2px 8px',
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 700,
            backgroundColor: 'var(--rv-accent-indigo-bg)',
            color: 'var(--rv-accent-indigo)',
        },
        deleteButton: {
            flexShrink: 0,
        },
    })
);

interface FileTypeTreatment {
    label: string;
    color: string;
}

const DEFAULT_FILE_TREATMENT: FileTypeTreatment = { label: 'FILE', color: 'var(--rv-accent-grey)' };

// Same colour (and now the same real file-type badge shape) a person already associates with
// each type from Office/Outlook/a PDF reader - reusing this app's own --rv-accent-* tokens
// rather than inventing a new palette. See FileTypeIcon.tsx for the shared page+band glyph.
const EXTENSION_TREATMENTS = new Map<string, FileTypeTreatment>([
    ['pdf', { label: 'PDF', color: 'var(--rv-accent-red)' }],
    ['doc', { label: 'DOC', color: 'var(--rv-accent-blue)' }],
    ['docx', { label: 'DOC', color: 'var(--rv-accent-blue)' }],
    ['xls', { label: 'XLS', color: 'var(--rv-accent-green)' }],
    ['xlsx', { label: 'XLS', color: 'var(--rv-accent-green)' }],
    ['ppt', { label: 'PPT', color: 'var(--rv-accent-orange)' }],
    ['pptx', { label: 'PPT', color: 'var(--rv-accent-orange)' }],
    ['msg', { label: 'MSG', color: 'var(--rv-accent-purple)' }],
    ['zip', { label: 'ZIP', color: 'var(--rv-accent-indigo)' }],
    ['txt', { label: 'TXT', color: 'var(--rv-accent-grey)' }],
]);

function getFileTreatment(extension: string | undefined): FileTypeTreatment {
    const normalized = extension?.toLocaleLowerCase().replace(/^\./, '');
    return (normalized && EXTENSION_TREATMENTS.get(normalized)) || DEFAULT_FILE_TREATMENT;
}

interface DocumentCardProps {
    data: MatterDocument;
    sourceSystem: string;
    onDelete?: (documentId: number, matterId: number) => void;
}

export const DocumentCard: React.FC<DocumentCardProps> = (props) => {

    const classes = useStyles();

    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const matterDocumentUrl = useMatterDocumentUrlQuery('no-cache');

    useEffect(() => {
        if (matterDocumentUrl.data && !matterDocumentUrl.loading && !matterDocumentUrl.error) {

            const { documentUrl } = matterDocumentUrl.data.matter;

            if (documentUrl) {
                const win = window.open(documentUrl, '_blank');
                if (win) {
                    win.focus();
                }
            }
        }
    },        [matterDocumentUrl.data, matterDocumentUrl.error, matterDocumentUrl.loading]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();

        let doc = props.data;

        matterDocumentUrl.getData({
            variables: {
                ...doc
            }
        });
    };

    const isDeleteDocumentEnabled = () => {
        let isDelete = true;
        if (props.sourceSystem.toLowerCase() === 'leap' || props.sourceSystem.toLowerCase() === 'filepro') {
            isDelete = false;
        }
        return isDelete;
    };

    const onDelete = () => {
        setShowConfirm(true);
    };

    const onDeleteConfirm = (agree: boolean) => {
        if (agree && props.onDelete) {
            const {documentId, matterId} = props.data;
            props.onDelete(documentId, matterId);
        }
        setShowConfirm(false);
    };

    if (props.data) {

        const { data } = props;

        const { label, color } = getFileTreatment(props.data.originatingType);

        return (
            <>
                {showConfirm && (
                    <DialogBox
                        title={props.data.title}
                        // tslint:disable-next-line:max-line-length
                        content={'Are you sure you want to delete?'}
                        show={showConfirm}
                        isAgree={onDeleteConfirm}
                        disAgreeLabel={'No'}
                        agreeLabel={'Yes'}
                    />
                )}
                <div className={classes.card}>
                    <span className={classes.iconChip} onClick={handleClick}>
                        <FileTypeIcon label={label} accentColor={color} />
                    </span>
                    <div className={classes.details}>
                        <RvLabel
                            className={classes.title}
                            label={data.title}
                            onClick={handleClick}
                        />
                        <div className={classes.meta}>
                            <span className={classes.metaLabel}>
                                {MatterTerminology.UploadedOn}{' '}
                                <FormattedDateTime
                                    value={data.creationDate}
                                    displayStyle={DateTimeDisplayStyle.ShortDateTime}
                                    DisplayInUTC={false}
                                />
                            </span>
                            {data.addedBy && (
                                <span className={classes.addedByChip}>{MatterTerminology.By.trim()} {data.addedBy}</span>
                            )}
                        </div>
                    </div>
                    {isDeleteDocumentEnabled() && (
                        <IconButton size="small" className={classes.deleteButton} onClick={onDelete}>
                            <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                        </IconButton>
                    )}
                </div>
            </>
        );
    }

    return (
        <div />
    );
};
